import { readFileSync, writeFileSync, mkdirSync, existsSync } from "node:fs";
import { join } from "node:path";
import yaml from "js-yaml";
import { IntentSchema, type IntentConfig } from "./schema.js";

const INTENT_DIR = ".fathom";
const INTENT_FILE = "intent.yaml";

function intentPath(dir: string): string {
  return join(dir, INTENT_DIR, INTENT_FILE);
}

/**
 * Load and validate intent from a project directory.
 * Reads `.fathom/intent.yaml`, parses YAML, validates with Zod.
 */
export async function loadIntent(dir: string): Promise<IntentConfig> {
  const filePath = intentPath(dir);

  if (!existsSync(filePath)) {
    throw new Error(`Intent file not found: ${filePath}`);
  }

  const content = readFileSync(filePath, "utf-8");
  const raw = yaml.load(content, { schema: yaml.JSON_SCHEMA });
  return IntentSchema.parse(raw);
}

/**
 * Validate and save intent to a project directory.
 * Writes validated YAML to `.fathom/intent.yaml`. Creates `.fathom/` if needed.
 */
export async function saveIntent(dir: string, intent: IntentConfig): Promise<void> {
  // Validate before writing
  const validated = IntentSchema.parse(intent);

  const fathomDir = join(dir, INTENT_DIR);
  if (!existsSync(fathomDir)) {
    mkdirSync(fathomDir, { recursive: true });
  }

  const content = yaml.dump(validated, {
    lineWidth: 120,
    noRefs: true,
    quotingType: '"',
    forceQuotes: false,
  });

  writeFileSync(intentPath(dir), content, "utf-8");
}

/**
 * Synchronously check if `.fathom/intent.yaml` exists in a directory.
 */
export function detectIntent(dir: string): boolean {
  return existsSync(intentPath(dir));
}
